'use client';

import { sendGAEvent } from '@next/third-parties/google';
import { useEffect, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { SessionData } from '@auth0/nextjs-auth0/types';
import { useMediaRemote } from '@vidstack/react';
import { usePlayer } from 'contexts/PlayerContext';
import styles from './EligibilityModal.module.scss';
import CloseButton from './eligibility-modal/CloseButton';

interface LoginSuccessModalProps {
  session: SessionData | null;
}

const MODAL_WHITELIST = ['/'];

export default function LoginSuccessModal({ session }: LoginSuccessModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const { player } = usePlayer();
  const remote = useMediaRemote(player);

  const handleAutoPlayBlocked = () => {
    if (session && !MODAL_WHITELIST.includes(pathname as string)) {
      setIsOpen(true);
    }
  };
  const hasLoginSuccessFlag = searchParams.get('loginSuccess') === 'true';

  useEffect(() => {
    if (hasLoginSuccessFlag && pathname === '/epo') {
      sendGAEvent('event', 'signupSuccess', {
        value: 'true',
      });
    }
  }, [hasLoginSuccessFlag, pathname]);

  useEffect(() => {
    if (player?.current && hasLoginSuccessFlag) {
      player.current.addEventListener('auto-play-fail', handleAutoPlayBlocked);
    }
  }, [player]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    if (player?.current) {
      player.current.removeEventListener(
        'auto-play-fail',
        handleAutoPlayBlocked
      );
    }

    remote.play();

    // Close the modal after attempting to resume
    setIsOpen(false);
  };

  const handleOverlayKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className={styles.overlay}
        onClick={handleClose}
        onKeyDown={handleOverlayKeyDown}
        role="button"
        tabIndex={0}
        aria-label="Close modal"
      />
      {/* Modal */}
      <div className={styles.modalWrapper}>
        <CloseButton onClose={handleClose} />
        <div className={styles.modal}>
          {/* Top accent line */}
          <div className={styles.accentLine} />
          <div className={styles.content}>
            <div className={styles.header}>
              <h2 className={styles.title}>
                You&apos;ve successfully logged in!
              </h2>
              <p className={styles.description}>
                Welcome back! You can now access all your saved plans and
                personalized features.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
